import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Action, Subject } from '@erp/authz';
import { useCan } from '@/hooks/useCan';
import { useUpdateLineMaxJoins } from 'src/api/rpc/hooks/procurement/useMillableClassifications';
import { MAX_JOINS, type SalesOrderLineItem } from '@erp/rpc';
import {
  effectiveMaxJoins,
  formatArchetype,
  formatContinuity,
} from './maxJoinsFormat';

function archetypeBadgeClass(
  archetype: SalesOrderLineItem['demandArchetype'],
): string {
  if (archetype === 'whole_package') {
    return 'rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-700';
  }
  if (archetype === 'cut_to_length') {
    return 'rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800';
  }
  return 'rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800';
}

function parseMaxJoinsDraft(draft: string): number | null | undefined {
  const trimmed = draft.trim();
  if (trimmed === '') return null;
  const next = Number(trimmed);
  if (!Number.isInteger(next) || next < 0 || next > MAX_JOINS) return undefined;
  return next;
}

function EditorFeedback({
  rangeError,
  saveFailed,
}: {
  rangeError: boolean;
  saveFailed: boolean;
}) {
  if (rangeError) {
    return (
      <p className="text-[11px] text-amber-800">
        <T context="maxJoins out of range">Enter 0–9, or blank to inherit.</T>
      </p>
    );
  }
  if (saveFailed) {
    return (
      <p className="text-[11px] text-amber-800">
        <T context="maxJoins save failed">Could not save continuity.</T>
      </p>
    );
  }
  return null;
}

function ContinuityEditor({
  salesOrderId,
  lineId,
  current,
  draft,
  setDraft,
}: {
  salesOrderId: string;
  lineId: string;
  current: number | null;
  draft: string;
  setDraft: (value: string) => void;
}) {
  const gt = useGT();
  const update = useUpdateLineMaxJoins();
  const [rangeError, setRangeError] = useState(false);
  const save = () => {
    const next = parseMaxJoinsDraft(draft);
    if (next === undefined) {
      setRangeError(true);
      return;
    }
    setRangeError(false);
    if (next === current) return;
    update.mutate({ salesOrderId, lineItemId: lineId, maxJoins: next });
  };
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <Input
          type="number"
          min={0}
          max={MAX_JOINS}
          step={1}
          className="h-7 w-16 px-1.5 text-xs"
          value={draft}
          placeholder={gt('inherit', {
            context: 'empty maxJoins input inherits SKU default',
          })}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          disabled={update.isPending}
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          disabled={
            update.isPending ||
            draft === (current != null ? String(current) : '')
          }
          onClick={save}
        >
          <T context="save maxJoins override">Save</T>
        </Button>
      </div>
      <EditorFeedback rangeError={rangeError} saveFailed={update.isError} />
    </div>
  );
}

interface Props {
  salesOrderId: string;
  line: Pick<
    SalesOrderLineItem,
    | 'id'
    | 'maxJoins'
    | 'productDefaultMaxJoins'
    | 'demandArchetype'
    | 'millableStatus'
  >;
  compact?: boolean;
}

export function MaxJoinsCell({ salesOrderId, line, compact = false }: Props) {
  const gt = useGT();
  const [canUpdate] = useCan(Action.Update, Subject.SalesOrder);
  const inherited =
    line.maxJoins == null && line.productDefaultMaxJoins != null;
  const effective = effectiveMaxJoins(line);
  const [draft, setDraft] = useState(
    line.maxJoins != null ? String(line.maxJoins) : '',
  );

  return (
    <div className={compact ? 'space-y-1' : 'space-y-1.5'}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-medium text-gray-800">
          {formatContinuity(effective, gt)}
        </span>
        {inherited ? (
          <span className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-800">
            <T context="maxJoins inherited from confirmed SKU default">
              SKU default
            </T>
          </span>
        ) : null}
        {line.demandArchetype ? (
          <span className={archetypeBadgeClass(line.demandArchetype)}>
            {formatArchetype(line.demandArchetype, gt)}
          </span>
        ) : null}
      </div>
      {canUpdate ? (
        <ContinuityEditor
          salesOrderId={salesOrderId}
          lineId={line.id}
          current={line.maxJoins ?? null}
          draft={draft}
          setDraft={setDraft}
        />
      ) : null}
    </div>
  );
}
